/**
 * RecordCommentsThread — a lightweight chat thread for a single record.
 * Visible to: admins + record creator + any workflow approver of the form.
 */
import { useEffect, useState, useCallback } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { PaperPlaneTilt, Trash, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";

function relTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
}

export function RecordCommentsThread({ formId, recordId, compact = false }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [draft, setDraft] = useState("");
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [allowed, setAllowed] = useState(true);

    const load = useCallback(async () => {
        if (!formId || !recordId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/forms/${formId}/records/${recordId}/comments`);
            setItems(data || []);
            setAllowed(true);
        } catch (e) {
            if (e?.response?.status === 403) { setAllowed(false); setItems([]); }
            else { toast.error(extractError(e)); }
        } finally { setLoading(false); }
    }, [formId, recordId]);

    useEffect(() => { load(); }, [load]);

    const send = async (e) => {
        e?.preventDefault();
        const body = draft.trim();
        if (!body) return;
        setSending(true);
        try {
            const { data } = await api.post(`/forms/${formId}/records/${recordId}/comments`, { body });
            setItems((cur) => [...cur, data]);
            setDraft("");
        } catch (err) { toast.error(extractError(err)); }
        finally { setSending(false); }
    };

    const del = async (cid) => {
        if (!confirm("Delete this comment?")) return;
        try {
            await api.delete(`/forms/${formId}/records/${recordId}/comments/${cid}`);
            setItems((cur) => cur.filter((c) => c.id !== cid));
        } catch (e) { toast.error(extractError(e)); }
    };

    if (!recordId) return null;
    if (!allowed) return null;

    const isAdmin = user?.role === "platform_admin" || user?.role === "org_admin";

    return (
        <div className={compact ? "" : "border-t border-border pt-5 mt-5"} data-testid="record-comments-thread">
            <div className="flex items-center gap-2 mb-3">
                <ChatCircleDots size={14} weight="duotone" className="text-primary" />
                <div className="label-overline">Discussion</div>
                <Badge variant="outline" className="rounded-sm font-mono text-[10px]" data-testid="comments-count">{items.length}</Badge>
            </div>

            {loading ? (
                <div className="text-xs text-muted-foreground py-2">Loading comments…</div>
            ) : items.length === 0 ? (
                <div className="text-xs text-muted-foreground border border-dashed border-border rounded-sm p-3 mb-3" data-testid="comments-empty">
                    No comments yet. Be the first to drop a note for the approvers.
                </div>
            ) : (
                <ul className="space-y-2 mb-3 max-h-72 overflow-y-auto pr-1" data-testid="comments-list">
                    {items.map((c) => {
                        const mine = c.user_id === user?.id;
                        const canDelete = mine || isAdmin;
                        return (
                            <li key={c.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={`comment-${c.id}`}>
                                <div className={`shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-sm border border-border text-xs font-semibold ${mine ? "bg-primary/10 text-primary" : "bg-secondary text-foreground"}`}>
                                    {(c.user_name || c.user_email || "?").slice(0, 1).toUpperCase()}
                                </div>
                                <div className={`flex-1 min-w-0 ${mine ? "text-right" : ""}`}>
                                    <div className={`inline-block max-w-[88%] text-left border border-border rounded-sm px-3 py-2 ${mine ? "bg-primary/5" : "bg-card"}`}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs font-medium">{c.user_name || c.user_email}</span>
                                            <span className="text-[10px] font-mono text-muted-foreground">{relTime(c.created_at)}</span>
                                            {canDelete && (
                                                <button type="button" onClick={() => del(c.id)} className="text-[10px] text-muted-foreground hover:text-destructive ml-1" data-testid={`del-comment-${c.id}`} title="Delete">
                                                    <Trash size={10} />
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-sm whitespace-pre-wrap break-words">{c.body}</p>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            <form onSubmit={send} className="flex items-end gap-2" data-testid="comment-composer">
                <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
                    placeholder="Write a note for the approvers… (⌘/Ctrl+Enter to send)"
                    rows={2}
                    className="rounded-sm flex-1 resize-none text-sm"
                    data-testid="comment-input"
                />
                <Button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground h-9"
                    data-testid="comment-send-btn"
                >
                    <PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending…" : "Send"}
                </Button>
            </form>
        </div>
    );
}
